import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';
import { routePath } from '../i18n/routes';
import LanguageSwitcher from './LanguageSwitcher';

const sections = /* i18n:extract */ [
  {
    paragraphs: [
      'Cette Politique de Confidentialité décrit comment OptimHub (ou "nous", "notre" ou "nos") collecte, utilise et protège les informations personnelles des visiteurs de www.optimhub.com (le "Site").'
    ]
  },
  {
    title: '1. Informations que Nous Collectons',
    paragraphs: [
      'Lorsque vous visitez le Site, nous collectons automatiquement certaines informations sur votre appareil, y compris votre navigateur web, votre adresse IP, votre fuseau horaire et certains des cookies installés sur votre appareil. Lorsque vous naviguez sur le Site, nous suivons également les pages que vous visitez et la façon dont vous interagissez avec le Site pour nous aider à améliorer notre service et comprendre les préférences des utilisateurs.'
    ]
  },
  {
    title: '2. Utilisation de Vos Informations',
    paragraphs: ['Nous utilisons les informations que nous collectons pour :'],
    list: [
      'Améliorer et optimiser notre Site (par exemple, en générant des analyses sur la façon dont nos visiteurs naviguent et interagissent avec le Site) ;',
      'Évaluer le succès de nos campagnes marketing et promotionnelles ;',
      'Améliorer la sécurité et la fonctionnalité de notre Site ;',
      'Respecter les obligations légales et les exigences réglementaires.'
    ]
  },
  {
    title: '3. Cookies et Technologies de Suivi',
    paragraphs: [
      "Nous utilisons des cookies et des technologies de suivi similaires pour suivre l'activité sur notre Site et maintenir certaines informations. Les cookies sont des fichiers contenant une petite quantité de données qui peuvent inclure un identifiant unique anonyme. Vous pouvez demander à votre navigateur de refuser tous les cookies ou d'indiquer quand un cookie est envoyé. Cependant, si vous n'acceptez pas les cookies, vous pourriez ne pas pouvoir utiliser certaines parties de notre Site."
    ]
  },
  {
    title: '4. Partage de Vos Informations',
    paragraphs: [
      "Nous pouvons partager vos informations personnelles avec des prestataires de services tiers qui nous aident à exploiter notre Site, à mener nos affaires ou à servir nos utilisateurs, tant que ces parties acceptent de garder ces informations confidentielles. Nous pouvons également divulguer des informations lorsque leur divulgation est appropriée pour se conformer à la loi, faire respecter nos politiques de site, ou protéger nos droits, notre propriété ou notre sécurité ou ceux d'autrui."
    ]
  },
  {
    title: '5. Sécurité des Données',
    paragraphs: [
      "Nous nous engageons à assurer la sécurité de vos informations. Afin de prévenir l'accès non autorisé ou la divulgation, nous avons mis en place des procédures physiques, électroniques et de gestion appropriées pour sauvegarder et sécuriser les informations que nous collectons en ligne."
    ]
  },
  {
    title: '6. Vos Droits',
    paragraphs: [
      "Selon votre lieu de résidence, vous pouvez avoir certains droits concernant les informations personnelles que nous détenons à votre sujet. Ceux-ci peuvent inclure le droit d'accéder, de mettre à jour ou de supprimer les informations, ainsi que d'autres droits. Si vous souhaitez exercer ces droits, veuillez nous contacter aux coordonnées fournies ci-dessous."
    ]
  },
  {
    title: '7. Modifications de cette Politique de Confidentialité',
    paragraphs: [
      'Nous pouvons mettre à jour notre Politique de Confidentialité de temps à autre. Nous vous informerons de tout changement en publiant la nouvelle Politique de Confidentialité sur cette page. Nous vous préviendrons par e-mail et/ou par un avis proéminent sur notre Site, avant que le changement ne devienne effectif et mettrons à jour la "date d\'entrée en vigueur" en haut de cette Politique de Confidentialité. Il vous est conseillé de consulter périodiquement cette Politique de Confidentialité pour tout changement. Les modifications de cette Politique de Confidentialité sont effectives lorsqu\'elles sont publiées sur cette page.'
    ]
  }
] /* i18n:end */;

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Button
              onClick={() => navigate(routePath('home', currentLanguage))}
              variant="ghost"
              size="sm"
              className="mr-4"
              data-testid="privacy-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {l('Retour')}
            </Button>
            <img
              src="https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png"
              alt="OptimHub"
              className="h-8 w-auto"
            />
            <div className="ml-auto">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-3xl font-bold text-gray-900 mb-2" data-testid="privacy-title">
              {l('Politique de Confidentialité')}
            </CardTitle>
            <p className="text-gray-600">OptimHub - {l("Date d'entrée en vigueur")} : 01/01/2020</p>
          </CardHeader>

          <CardContent className="prose prose-lg max-w-none">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <section key={index}>
                  {section.title && (
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{l(section.title)}</h2>
                  )}
                  {section.paragraphs?.map((paragraph, i) => (
                    <p key={i} className="text-gray-700 leading-relaxed mb-4">
                      {l(paragraph)}
                    </p>
                  ))}
                  {section.list && (
                    <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-relaxed">
                      {section.list.map((item, i) => (
                        <li key={i}>{l(item)}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">{l('8. Nous Contacter')}</h2>
                <div className="text-gray-700 leading-relaxed space-y-2">
                  <p>{l('Si vous avez des questions ou des préoccupations concernant cette Politique de Confidentialité, veuillez nous contacter à :')}</p>
                  <p>
                    <strong>{l('Email')} :</strong>{' '}
                    <a href="mailto:dpo@optimhub.com" className="text-blue-600 hover:text-blue-800">dpo@optimhub.com</a>
                  </p>
                  <p>
                    <strong>{l('Adresse')} :</strong> 11 rue de Lourmel 75015 PARIS
                  </p>
                </div>
              </section>

              <section>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {l('En utilisant notre Site, vous consentez à notre Politique de Confidentialité et acceptez ses termes.')}
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {l("OPTIMHUB obtient votre consentement conformément aux politiques et spécifications techniques du Transparency & Consent Framework d'IAB Europe. Il utilise la Consent Management Platform n°92.")}
                  </p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
